import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmailTaskStatus } from '../../../models/deps';
import { getEmailTasks, SendQueuedEmails, updateEmailTaskStatus } from '../../../api/General/Notification';
import moment from 'moment';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '../../../components/ui/Table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter
} from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { Spinner } from '../../../components/ui/Spinner';
import Pagination from '../../../components/Pagination';
import { PageLayout } from '../../../components/ui/PageLayout';
import { Mail } from 'lucide-react';

/**
 * Wire shape of GET /api/Notification/EmailTasks.
 *
 * The endpoint is Operator-only but the EmailQueuerTasks table has no CompanyId, so it spans every
 * tenant. It used to return the raw EF entity, which published each tenant's employee email
 * addresses and — via ModelJson, a snapshot of the RazorLight template model — the PLAINTEXT
 * temporary password from the welcome mail. The server now projects to EmailQueuerTaskListItemView
 * and drops `to` / `cc` / `bcc` / `attachmentPaths` / `modelJson` / `modelType` / `subject`
 * entirely, exposing only `toMasked` (first character + domain). `subject` is gone because it is
 * built from notification titles with interpolated employee names, so it is PII, not a static
 * template label — `template` is the PII-free triage key that replaces it.
 *
 * Declared locally rather than in models/deps.ts: `EmailQueuerTask` there mirrors the library
 * entity and is not the wire contract of this screen.
 */
interface EmailTaskListItem {
  id: number;
  toMasked: string;
  template: string;
  status: EmailTaskStatus;
  retryCount?: number | null;
  createdOn: string | null;
  sentOn: string | null;
  nextAttemptOn?: string | null;
}

const SystemEmails: React.FC = () => {
  const { t } = useTranslation();
  const [emailTasks, setEmailTasks] = useState<EmailTaskListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState<EmailTaskListItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<EmailTaskStatus>(EmailTaskStatus.Pending);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  useEffect(() => {
    fetchEmailTasks();
  }, []);

  const fetchEmailTasks = async () => {
    setLoading(true);
    try {
      const response = await getEmailTasks();
      if (response.status === 200) {
        setEmailTasks((response.payload || []) as unknown as EmailTaskListItem[]);
      } else {
        console.error('Failed to load email tasks:', response.errors);
      }
    } catch (error) {
      console.error('Error fetching email tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const showUpdateStatusModal = (task: EmailTaskListItem) => {
    setSelectedTask(task);
    setNewStatus(task.status);
    setIsModalOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedTask) return;
    try {
      const response = await updateEmailTaskStatus(selectedTask.id, newStatus);
      if (response.status === 200) {
        setIsModalOpen(false);
        fetchEmailTasks();
      }
    } catch (error) {
      console.error('Error updating email status:', error);
    }
  };

  const sendQueuedEmails = async () => {
    setLoading(true);
    try {
      const response = await SendQueuedEmails();
      if (response.status === 200) {
        fetchEmailTasks();
      }
    } catch (error) {
      console.error('Error sending queued emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: EmailTaskStatus) => {
    switch (status) {
      case EmailTaskStatus.Pending:
        return <StatusBadge variant="info">{t('common:Pending')}</StatusBadge>;
      case EmailTaskStatus.Sending:
        return <StatusBadge variant="warning">{t('common:Sending')}</StatusBadge>;
      case EmailTaskStatus.Sent:
        return <StatusBadge variant="success">{t('common:Sent')}</StatusBadge>;
      case EmailTaskStatus.Error:
        return <StatusBadge variant="error">{t('common:Error')}</StatusBadge>;
      case EmailTaskStatus.PermanentlyFailed:
        return <StatusBadge variant="error">{t('common:Permanently Failed')}</StatusBadge>;
      default:
        return <StatusBadge variant="neutral">{t('common:Unknown') || 'Unknown'}</StatusBadge>;
    }
  };

  const totalPages = Math.ceil(emailTasks.length / rowsPerPage);
  const paginatedTasks = emailTasks.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  // CO-231: `PermanentlyFailed` belongs here, and it is not cosmetic. The queue loop drains rows
  // serially, so a row stuck in `Sending` blocks every Pending row behind it; forcing that row to a
  // TERMINAL status is what unblocks the queue (this is exactly how dev row 9925 was released).
  // Without this option the Operator could only move a stuck row between non-terminal statuses.
  // Moving a row back to `Pending`/`Error` re-arms it: the server clears RetryCount/NextAttemptOn so
  // the row is genuinely re-picked (see NotificationService.UpdateEmailTaskStatusAsync).
  const statusOptions = [
    { value: EmailTaskStatus.Pending, label: t('common:Pending') },
    { value: EmailTaskStatus.Sending, label: t('common:Sending') },
    { value: EmailTaskStatus.Sent, label: t('common:Sent') },
    { value: EmailTaskStatus.Error, label: t('common:Error') },
    { value: EmailTaskStatus.PermanentlyFailed, label: t('common:Permanently Failed') },
  ];

  return (
    <PageLayout
      title={t('common:System Emails')}
      icon={Mail}
      accentColor="slate"
      headerActions={(
        <div className="flex gap-2">
          <Button
            variant="outline"
            iconName="Send"
            onClick={sendQueuedEmails}
            disabled={loading}
          >
            {loading ? <Spinner size="sm" /> : t('common:Send Queued Emails')}
          </Button>
          <Button
            variant="outline"
            iconName="RotateCcw"
            onClick={fetchEmailTasks}
            disabled={loading}
          >
            {loading ? <Spinner size="sm" /> : t('common:Refresh')}
          </Button>
        </div>
      )}
    >
        <div className="rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('common:ID')}</TableHead>
                <TableHead>{t('common:To')}</TableHead>
                <TableHead>{t('common:Template')}</TableHead>
                <TableHead>{t('common:Status')}</TableHead>
                <TableHead>{t('common:Created On')}</TableHead>
                <TableHead>{t('common:Sent On')}</TableHead>
                <TableHead>{t('common:Actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center">
                    <Spinner size="md" />
                  </TableCell>
                </TableRow>
              ) : emailTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center">
                    {t('common:No email tasks found')}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedTasks.map((task) => (
                  <TableRow key={task.id}>
                    <TableCell>{task.id}</TableCell>
                    <TableCell>
                      {task.toMasked && task.toMasked.length > 30
                        ? `${task.toMasked.substring(0, 30)}...`
                        : task.toMasked}
                    </TableCell>
                    <TableCell>{task.template}</TableCell>
                    <TableCell>{getStatusBadge(task.status)}</TableCell>
                    <TableCell>
                      {task.createdOn ? moment(task.createdOn).format('YYYY-MM-DD HH:mm:ss') : ''}
                    </TableCell>
                    <TableCell>
                      {task.sentOn ? moment(task.sentOn).format('YYYY-MM-DD HH:mm:ss') : t('common:Not sent yet')}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => showUpdateStatusModal(task)}
                      >
                        <span className="sr-only">{t('common:Edit')}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <div className="p-4 border-t border-border">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t('common:Update Email Status')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t('common:Email to')}: {selectedTask?.toMasked}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('common:Current Status')}: {selectedTask && getStatusBadge(selectedTask.status)}
            </p>
            <Select
              label={t('common:New Status')}
              options={statusOptions}
              value={newStatus}
              onChange={(val: any) => setNewStatus(val as EmailTaskStatus)}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              {t('common:Cancel')}
            </Button>
            <Button onClick={handleUpdateStatus}>
              {t('common:Update')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default SystemEmails;
